import { useEffect, useRef, useState } from 'react';
import { formatTimer, type BabyEvent } from '@bb/shared/core';
import { Icon, useNow } from '../components';
import { currentRunSec, currentSide, runsOf, setAlarm, setSideTarget, stopTimer, switchBreast } from '../actions';
import { categoryOf } from '../lib';
import { platform } from '../platform';
import { useApp } from '../state';
import { RUNNING_TITLE, TimerSticker, activeSeconds, countdownLeft } from './Running';


/**
 * Таймеры-стикеры сверху: тот же цветной фон, что у большой карточки. На вкладках — всегда сверху;
 * на «Сегодня» (floating) — когда большие карточки ушли за экран.
 */
export function TimerBar({ floating = false }: { floating?: boolean }) {
  const ctx = useApp();
  if (!ctx.running.length) return null;
  return (
    <div className={`timerbar${floating ? ' floating' : ''}`} role="status" aria-label="Идущие таймеры">
      {ctx.running.map((e) => (
        <TimerSticker key={e.id} e={e} />
      ))}
    </div>
  );
}

/** На «Сегодня»: большие карточки таймеров ушли под закреплённую шапку — показываем стикер. */
export function useOutOfView<T extends HTMLElement>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [out, setOut] = useState(false);
  useEffect(() => {
    const check = () => {
      const el = ref.current;
      if (!el) return setOut(false);
      const header = document.querySelector('.sticky-top')?.getBoundingClientRect().bottom ?? 0;
      setOut(el.getBoundingClientRect().bottom < header + 8);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);
  return [ref, out];
}

/* ---------- Сигнал таймера ---------- */

function beep() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ac = new Ctx();
    [0, 0.35, 0.7].forEach((t) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ac.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.3, ac.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + t + 0.25);
      o.connect(g).connect(ac.destination);
      o.start(ac.currentTime + t);
      o.stop(ac.currentTime + t + 0.3);
    });
    setTimeout(() => void ac.close(), 1500);
  } catch {
    /* звук недоступен */
  }
}

/**
 * Когда таймер дошёл до нуля и приложение открыто: экран-сигнал с понятными кнопками, звук и вибрация.
 * Бот в это же время присылает сообщение (на случай, если приложение закрыто).
 */
export function AlarmWatcher() {
  const ctx = useApp();
  const fired = useRef(new Set<string>());
  const [ring, setRing] = useState<BabyEvent | null>(null);
  const now = useNow(1000);
  useEffect(() => {
    for (const e of ctx.running) {
      const left = countdownLeft(e, now);
      // Для груди сигнал — на каждый подход: после смены груди может прозвучать снова.
      const key = e.type === 'breast' ? `${e.id}:${runsOf(e).length}:${String(e.payload.sideTargetSec)}` : `${e.id}:${String(e.payload.alarmAt)}`;
      if (left != null && left <= 0 && left > -600 && !e.payload.pausedAt && !fired.current.has(key)) {
        fired.current.add(key);
        platform.haptic('warning');
        try {
          navigator.vibrate?.([300, 150, 300]);
        } catch {
          /* нет вибрации */
        }
        beep();
        setRing(e);
      }
    }
  }, [now, ctx.running]);

  const live = ring ? ctx.running.find((r) => r.id === ring.id) : null;
  useEffect(() => {
    if (ring && !live) setRing(null);
  }, [ring, live]);
  if (!ring || !live) return null;
  const title = live.type === 'sleep' ? 'Пора будить?' : live.type === 'walk' ? 'Пора домой?' : 'Время вышло';
  const what =
    live.type === 'breast'
      ? `${currentSide(live) === 'left' ? 'Левая' : 'Правая'} грудь — ${formatTimer(currentRunSec(live))}, всего ${formatTimer(activeSeconds(live, Date.now()))}`
      : live.type === 'bottle'
        ? `Кормление из бутылочки идёт ${formatTimer(activeSeconds(live, Date.now()))}`
        : live.type === 'sleep'
          ? `Малыш спит уже ${formatTimer(activeSeconds(live, Date.now()))}`
          : `Прогулка идёт ${formatTimer(activeSeconds(live, Date.now()))}`;
  const close = () => setRing(null);
  return (
    <div className="alarm-scrim" role="alertdialog" aria-modal="true" aria-label={title} data-testid="alarm-modal">
      <div className={`alarm-card cat-${categoryOf(live.type)}`}>
        <div className="alarm-icon" aria-hidden="true">
          ⏰
        </div>
        <h2 className="h1" style={{ textAlign: 'center' }}>
          {title}
        </h2>
        <p style={{ textAlign: 'center', margin: 0 }}>
          {what}
        </p>
        <div className="stack" style={{ width: '100%' }}>
          {live.type === 'breast' && (
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => {
                close();
                void switchBreast(ctx, live);
              }}
              data-testid="alarm-switch"
            >
              <Icon name="swap" size={18} /> Сменить на {currentSide(live) === 'left' ? 'правую' : 'левую'}
            </button>
          )}
          {live.type === 'bottle' ? (
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => {
                close();
                ctx.openAction('bottle', live);
              }}
            >
              <Icon name="stop" size={18} /> Поел — записать
            </button>
          ) : (
            <button
              type="button"
              className={`btn ${live.type === 'breast' ? 'btn-secondary' : 'btn-primary'} btn-block`}
              onClick={() => {
                close();
                void stopTimer(ctx, live);
              }}
            >
              <Icon name="stop" size={18} /> {live.type === 'sleep' ? 'Проснулся' : live.type === 'walk' ? 'Вернулись домой' : 'Закончить кормление'}
            </button>
          )}
          <div className="tiles2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                close();
                if (live.type === 'breast') void setSideTarget(ctx, live, Math.round(currentRunSec(live)) + 5 * 60);
                else void setAlarm(ctx, live, 5 * 60, '5 мин');
              }}
            >
              Ещё 5 минут
            </button>
            <button type="button" className="btn btn-secondary" onClick={close}>
              Продолжить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
